export interface TenantConfig {
  key: string
  sportCategory: string
  brandName: string
  accentColor: string
}

// Mirror of config/tenants.yml (the server is the source of truth via
// window.__TENANT__). Used only as a client-side fallback so branding still
// resolves correctly when the injected tenant is absent.
const TENANTS: Record<string, TenantConfig> = {
  soccer: { key: 'soccer', sportCategory: 'soccer', brandName: 'D1 Soccer Hub', accentColor: '#22c55e' },
  volleyball: { key: 'volleyball', sportCategory: 'volleyball', brandName: 'D1 Volleyball Hub', accentColor: '#3b82f6' },
  baseball: { key: 'baseball', sportCategory: 'baseball', brandName: 'D1 Baseball Hub', accentColor: '#ef4444' },
  hockey: { key: 'hockey', sportCategory: 'hockey', brandName: 'D1 Hockey Hub', accentColor: '#8b5cf6' },
}

const DEFAULT_TENANT: TenantConfig = TENANTS.soccer

declare global {
  interface Window {
    __TENANT__?: TenantConfig
  }
}

// Derive the tenant from the hostname. This is the safety net for a stale
// cached HTML shell that predates the server-side __TENANT__ injection: without
// it the app would fall back to the soccer default on every hub, showing
// "D1 Soccer" branding on d1volleyballhub.com even though the (host-scoped) API
// returns volleyball data. Matching on hostname keeps the brand aligned to the
// URL. The sport keys are not substrings of one another, so a simple contains
// check is unambiguous.
function tenantFromHost(): TenantConfig | null {
  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  const key = Object.keys(TENANTS).find((k) => host.includes(k))
  return key ? TENANTS[key] : null
}

export function getTenant(): TenantConfig {
  return window.__TENANT__ || tenantFromHost() || DEFAULT_TENANT
}

const SPORT_LABELS: Record<string, { men: string; women: string }> = {
  soccer: { men: "Men's", women: "Women's" },
  volleyball: { men: "Men's", women: "Women's" },
  baseball: { men: 'Baseball', women: 'Softball' },
  hockey: { men: "Men's", women: "Women's" },
}

export function getSportLabel(sportCategory: string, urlSlug: string): string {
  const labels = SPORT_LABELS[sportCategory] || SPORT_LABELS.soccer
  return urlSlug === 'women' ? labels.women : labels.men
}

// Features that only exist for soccer — hidden on the other hubs.
const SOCCER_ONLY_NAV = new Set(['Bracketology', 'Transfers', 'Recruiting'])

// Features with no working data source, hidden everywhere until one exists.
// NCAA's RPI feed is bot-blocked (403), and Bubble is derived from RPI.
const DISABLED_NAV = new Set(['RPI', 'Bubble'])

export function isNavItemVisible(label: string, sportCategory: string): boolean {
  if (DISABLED_NAV.has(label)) return false
  if (SOCCER_ONLY_NAV.has(label) && sportCategory !== 'soccer') return false
  return true
}

export function getBrandShortName(brandName: string): string {
  return brandName.replace('D1 ', '').replace(' Hub', '')
}
